/**
 * SpacesSearchView - Reusable component for searching and displaying spaces
 *
 * This component can be used in both authenticated and unauthenticated contexts:
 * - Authenticated: Uses spaceManagementService.searchSpaces() for host/admin spaces
 * - Unauthenticated: Uses spaceManagementService.searchPublicSpacesUnauthenticated() for public listings
 *
 * Features:
 * - Multiple view modes: List (with map), Grid, Map-only
 * - Advanced filtering with IllustrativeFilterDialog
 * - Sorting and pagination
 * - Responsive design
 * - Category filtering
 * - Real-time search updates
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Grid3x3,
  List as ListIcon,
  Map as MapIcon,
  Building2,
  Star,
  Sparkles,
  Calendar,
  Users,
  SlidersHorizontal,
} from "lucide-react";
import SortDropdown from "./SortDropdown";
import { Button } from "../shadcn/ui/button";
import { Badge } from "../shadcn/ui/badge";
import HEmptyState from "../hcomponents/HEmptyState";
import Pagination from "./Pagination";
import PageHeader from "./PageHeader";
import ReusableMapView from "./ReusableMapView";
import GridView from "../screens/host-experience/host-spaces/components/GridView";
import SpaceCard from "../screens/host-experience/host-spaces/components/SpaceCard";
import SpaceCardSkeleton from "../screens/host-experience/host-spaces/components/SpaceCardSkeleton";
import SpacesSearchBar from "./SpacesSearchBar";
import SpaceSearchBar from "./SpaceSearchBar";
import CountryPickerBanner, { clearBrowseCountry, saveBrowseCountry, getSavedBrowseCountry } from "./CountryPickerBanner";
import CountrySelectorBadge from "./CountrySelectorBadge";
import { detectUnsupportedCountry, watchLocationPermission } from "../utils/locationUtils";
import IllustrativeFilterDialog from "./IllustrativeFilterDialog";
import spaceManagementService from "../services/host-apiServices/spaceManagementService";
import billingService from "../services/billingService";
import logger from "../utils/logger";
import * as localService from "../services/localService";
import { useCurrencies } from "../hooks/useCurrencies";
import { generateSpaceName, getEventName } from "../utils/spaceNamingUtils";
import { formatDurationType } from "../utils/bookingTimeframeUtils";
import { trackAnalyticsEvent, AnalyticsEvents, AnalyticsProps } from "../utils/analytics";

// Stable empty-array reference to avoid handing a new [] to children each render.
const EMPTY_ARRAY = [];

// Filter Chip Component with smooth animations
const FilterChip = ({ label, active = false, onClick, isVisible = true, icon: Icon }) => {
  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.button
          onClick={onClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.15,
            ease: "easeInOut",
          }}
          layout
          className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-150 ease-in-out flex items-center gap-1.5 ${
            active
              ? "bg-gray-900 text-white border border-gray-900 shadow-md"
              : "bg-white text-gray-900 hover:bg-gray-100 border border-gray-300"
          }`}
        >
          {Icon && <Icon className="w-4 h-4" />}
          {label}
        </motion.button>
      )}
    </AnimatePresence>
  );
};

/**
 * Main SpacesSearchView Component
 * @param {string} [mode="unauthenticated"] - The mode: "authenticated" or "unauthenticated"
 * @param {boolean} [showBackButton=true] - Whether to show the back button
 * @param {string} [title="Workspaces"] - The title to display
 * @param {string} [subtitle] - Optional subtitle
 * @param {object} [initialFilters={}] - Initial filter values
 * @param {function} [onSpaceClick] - Callback when a space is clicked
 * @param {number} [pageSize=10] - Number of items per page
 * @param {boolean} [showPublishedStatusFilter=false] - Whether to show published status filter
 * @param {Array<{label: string, path?: string}>} [breadcrumbs] - Optional custom breadcrumbs array
 */
const SpacesSearchView = ({
  mode = "unauthenticated",
  showBackButton: _showBackButton = true,
  title = "Workspaces",
  subtitle,
  initialFilters = {},
  onSpaceClick,
  pageSize: initialPageSize = 10,
  showPublishedStatusFilter = false, // Only Properties.jsx should set this to true
  breadcrumbs = null, // Optional custom breadcrumbs array: [{ label: "Home", path: "/home" }, { label: "Current" }]
}) => {
  // Generate default breadcrumbs based on mode and title if not provided
  const defaultBreadcrumbs = useMemo(() => {
    if (breadcrumbs) return breadcrumbs;

    if (mode === "authenticated") {
      // For authenticated mode (FilterProperties and Properties screens)
      return [{ label: "Home", path: "/host" }, { label: title || "Spaces & Services" }];
    } else {
      // For unauthenticated mode (WorkspacesSearch)
      return [{ label: "Home", path: "/home" }, { label: title }];
    }
  }, [mode, title, breadcrumbs]);

  // Authenticated: navigate to the host's profile page.
  // Unauthenticated: stay on the search results and re-run the public search scoped to that host.
  const history = useHistory();
  const handleHostClick = useCallback(
    space => {
      const hostId = space?.organization?.id || space?.hostOrganizationId || space?.organizationId;
      if (hostId && mode !== "authenticated") {
        setHostFilterLabel({ id: hostId, name: space?.organization?.name || space?.organizationName || "" });
        setSearchText("");
        setSearchParams(prev => {
          const next = { ...prev };
          delete next.addressContains;
          delete next.buildingName;
          delete next.spaceName;
          delete next.cityOrTown;
          delete next.country;
          delete next.hostOrgName;
          next.hostOrganizationId = hostId;
          return next;
        });
        setCurrentPage(1);

        // Keep the URL in sync so a refresh/shared link restores the host filter
        const query = new URLSearchParams(history.location.search);
        [
          "query",
          "search",
          "addressContains",
          "spaceName",
          "buildingName",
          "cityOrTown",
          "city",
          "country",
          "hostOrgName",
        ].forEach(key => query.delete(key));
        query.set("hostOrganizationId", String(hostId));
        history.replace({ search: query.toString() });
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (hostId) {
        const fromBreadcrumbs =
          defaultBreadcrumbs && defaultBreadcrumbs.length > 0
            ? [
                ...defaultBreadcrumbs.slice(0, -1),
                { label: defaultBreadcrumbs[defaultBreadcrumbs.length - 1].label, path: history.location.pathname },
              ]
            : undefined;
        history.push(`/hosts/${hostId}`, { organization: space?.organization || null, fromBreadcrumbs });
      }
    },
    [history, defaultBreadcrumbs, mode]
  );

  // State
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(initialPageSize);
  const [viewMode, setViewMode] = useState("list");
  // Sorting: default to newest-first, but allow parent to provide initial sort
  const [sortBy, setSortBy] = useState(() => initialFilters?.sortBy || initialFilters?.orderBy || "createdAt:desc");
  const [activeFilterIds, setActiveFilterIds] = useState(new Set()); // Track multiple active filter IDs
  const [activeFilters, setActiveFilters] = useState([]); // Track filter badges for display
  const [hostFilterLabel, setHostFilterLabel] = useState(null); // { id, name } of the host clicked on a card
  const [serviceCategories, setServiceCategories] = useState([]); // Store service categories from billing service
  const [serviceSubcategories, setServiceSubcategories] = useState([]); // Store subcategories locally
  const reduxCategories = useSelector(state => state.dashboard?.serviceCategories || []);
  const reduxSubcategories = useSelector(state => state.dashboard?.serviceSubcategories || []);
  const [showFilters, setShowFilters] = useState(false); // Control IllustrativeFilterDialog visibility
  const [showCountryBanner, setShowCountryBanner] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState("");
  const [selectedBrowseCountry, setSelectedBrowseCountry] = useState(() => getSavedBrowseCountry());

  // Resolve the visitor's country and open the country picker when we cannot
  // serve their market: outside our operating countries, or when the browser
  // location prompt was declined / unavailable. Never fall back to a default
  // market silently.
  useEffect(() => {
    let cancelled = false;
    detectUnsupportedCountry().then(res => {
      if (cancelled || !res) return;
      if (res.unsupported) {
        setShowCountryBanner(true);
        if (res.country) setDetectedCountry(res.country);
      }
    });

    // If the visitor revokes location access while the app is open we no longer
    // know their market, so surface the picker instead of continuing to serve
    // the previously detected one.
    const unwatch = watchLocationPermission(state => {
      if (cancelled || state !== "denied") return;
      setDetectedCountry("");
      setShowCountryBanner(true);
    });

    return () => {
      cancelled = true;
      unwatch();
    };
  }, []);
  const [filterState, setFilterState] = useState({
    priceRange: [0, 10000],
    capacity: 1,
    amenities: [],
    publishedStatus: [],
    featuredStatus: [],
    topSpaces: false,
    duration: [],
    categoryId: null,
    subCategoryId: null,
  });

  // Focused space for map zoom functionality
  const [focusedSpace, setFocusedSpace] = useState(null);

  const shouldShowSearchBar = !showPublishedStatusFilter && (mode === "authenticated" || mode === "unauthenticated");

  // Sort options - memoized to prevent recreation on every render
  const sortOptions = useMemo(
    () => [
      { field: "createdAt", label: "Created" },
      { field: "ratePrice", label: "Price" },
    ],
    []
  );

  // Ensure sortBy always matches an available option; fallback to newest
  const currentSort = useMemo(() => {
    return sortBy || "createdAt:desc";
  }, [sortBy]);

  const getPrimaryImageFromMedia = useCallback(space => {
    if (space?.media?.images?.length) {
      const first = space.media.images[0];
      if (typeof first === "string") {
        return first;
      }
      if (first?.url) {
        return first.url;
      }
    }

    if (space?.image) {
      return space.image;
    }

    return "/img/placeholder-space.jpg";
  }, []);

  const mapSpaceForDisplay = useCallback(
    space => {
      if (!space) return null;
      const firstInstance = space.instances?.[0];
      const buildingAddress = firstInstance?.location?.buildingAddress;

      const locationParts = [
        buildingAddress?.streetAddress,
        buildingAddress?.city,
        buildingAddress?.region,
        buildingAddress?.country,
      ].filter(Boolean);

      const location =
        locationParts.join(", ") ||
        firstInstance?.location?.name ||
        space.cityOrTown ||
        space.addressContains ||
        "Location unavailable";

      // Use the space's own currency field
      const spaceCurrency = space.currency || "KES";

      const pricing = (() => {
        if (space.ratePrice) {
          const unit = (space.duration || space.durationUnit || "").toString();
          return `${spaceCurrency} ${(space.ratePrice || 0).toLocaleString()} ${formatDurationType(unit)}`;
        }
        if (space.propertyPrice?.amount) {
          return `${spaceCurrency} ${(space.propertyPrice.amount || 0).toLocaleString()}`;
        }
        return null;
      })();

      // Use generateSpaceName utility for search results
      const currentUser = mode === "authenticated" ? localService.getUser() : null;
      const hostOrgName = currentUser?.organization?.name || currentUser?.organizationName;
      const spaceWithOrg = { ...space, organizationName: space.organizationName || hostOrgName };
      // PLAY services show their event name (stored as the instance name)
      const eventName = getEventName(space, firstInstance);
      // When hostDisplayName is set, use "subcategory - hostDisplayName" instead of org name
      const displayName = eventName || (space.hostDisplayName
        ? `${space.subCategory?.name || space.subCategoryName || space.description || "Space"} - ${space.hostDisplayName}`
        : generateSpaceName(spaceWithOrg, firstInstance, true));

      return {
        ...space,
        name: displayName,
        displayName: displayName,
        propertyTitle: space.propertyTitle || displayName,
        image: getPrimaryImageFromMedia(space),
        location,
        pricing,
        coordinates:
          buildingAddress?.latitude && buildingAddress?.longitude
            ? { latitude: buildingAddress.latitude, longitude: buildingAddress.longitude }
            : firstInstance?.location?.coordinates || null,
        instanceCount: space.availableInstancesCount || space.instances?.length || 0,
        hostDisplayName: space.hostDisplayName,
        hostDisplayImage: space.hostDisplayImage,
      };
    },
    [getPrimaryImageFromMedia]
  );

  const buildSuggestionFromSpace = useCallback(
    space => {
      const normalized = mapSpaceForDisplay(space);
      if (!normalized) return null;
      return {
        id: normalized.id,
        name: normalized.name,
        location: normalized.location,
        pricing: normalized.pricing,
        image: normalized.image,
        raw: normalized,
      };
    },
    [mapSpaceForDisplay]
  );

  // Search parameters - DO NOT hardcode status or featured!
  // Only include what the user explicitly selects or what's passed via initialFilters
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState(() => {
    if (showPublishedStatusFilter) {
      return "all";
    }
    // Read search filter from localStorage if available
    const storedFilters = localStorage.getItem("searchFilters");
    if (storedFilters) {
      try {
        const parsed = JSON.parse(storedFilters);
        return parsed.searchFilter || "all";
      } catch (e) {
        return "all";
      }
    }
    return "all";
  }); // "all", "organizations", "buildings", "spaces"
  const [searchText, setSearchText] = useState(() => {
    if (showPublishedStatusFilter) {
      return "";
    }
    // Initialize from localStorage if available
    const storedFilters = localStorage.getItem("searchFilters");
    if (storedFilters) {
      try {
        const parsed = JSON.parse(storedFilters);
        // Return any of the search fields that were set
        return parsed.addressContains || parsed.spaceName || parsed.buildingName || parsed.hostOrgName || "";
      } catch (e) {
        return "";
      }
    }
    return "";
  });
  const [spaceSuggestions, setSpaceSuggestions] = useState([]);
  const [isFetchingSpaceSuggestions, setIsFetchingSpaceSuggestions] = useState(false);
  const spaceSuggestionAbortRef = useRef(null);
  const spaceSuggestionDebounceRef = useRef(null);
  const wasDialogOpenRef = useRef(false);

  const [searchParams, setSearchParams] = useState(() => {
    if (showPublishedStatusFilter) {
      return {
        city: "",
        country: "",
        minPrice: "",
        maxPrice: "",
        minCapacity: "",
        ...initialFilters,
      };
    }

    // Read from localStorage for both modes - authenticated users coming from search should have filters passed through
    const storedFilters = localStorage.getItem("searchFilters");
    const storedDates = localStorage.getItem("searchDates");

    logger.log("🔎 SpacesSearchView: Reading from localStorage", {
      mode,
      storedFilters,
      storedDates,
    });

    let parsedFilters = {};
    let parsedDates = {};

    if (storedFilters) {
      try {
        parsedFilters = JSON.parse(storedFilters);
        logger.log("✅ Parsed filters:", parsedFilters);
        // DON'T clear filters here - let them persist until after first search
      } catch (e) {
        logger.error("Error parsing stored search filters:", e);
      }
    }

    if (storedDates) {
      try {
        parsedDates = JSON.parse(storedDates);
        logger.log("✅ Parsed dates:", parsedDates);
        // DON'T clear dates here - let them persist until after first search
      } catch (e) {
        logger.error("Error parsing stored search dates:", e);
      }
    }

    const finalParams = {
      city: "",
      country: "",
      minPrice: "",
      maxPrice: "",
      minCapacity: "",
      ...parsedFilters, // Fallback to filters stored in localStorage
      ...initialFilters, // URL parameters passed explicitly take precedence over stale localStorage
      // Ensure subCategoryId from initialFilters or parsedFilters is preserved
      ...((initialFilters.subCategoryId || parsedFilters.subCategoryId) && { 
        subCategoryId: initialFilters.subCategoryId || parsedFilters.subCategoryId 
      }),
      // Add date and time params from initialFilters or parsedDates (range & specific modes)
      ...((initialFilters.startDate || parsedDates.startDate) && { startDate: initialFilters.startDate || parsedDates.startDate }),
      ...((initialFilters.endDate || parsedDates.endDate) && { endDate: initialFilters.endDate || parsedDates.endDate }),
      ...((initialFilters.specificDate || parsedDates.specificDate) && { 
        specificDate: initialFilters.specificDate || parsedDates.specificDate 
      }),
      ...(parsedDates.startTime && parsedDates.startTime !== "" && { startTime: parsedDates.startTime }),
      ...(parsedDates.endTime && parsedDates.endTime !== "" && { endTime: parsedDates.endTime }),
    };

    // Normalize hydrated subcategory-filter option ids to a numeric array. Drop them if no
    // subcategory is in scope, since options belong to a specific subcategory (stale guard).
    if (finalParams.filterOptionIds != null && finalParams.subCategoryId) {
      const rawIds = Array.isArray(finalParams.filterOptionIds)
        ? finalParams.filterOptionIds
        : String(finalParams.filterOptionIds).split(",");
      const numericIds = rawIds.map(Number).filter(n => !Number.isNaN(n));
      if (numericIds.length > 0) {
        finalParams.filterOptionIds = numericIds;
      } else {
        delete finalParams.filterOptionIds;
      }
    } else {
      delete finalParams.filterOptionIds;
    }

    // Normalize hydrated operating-territory ids to a numeric array (MOVE category).
    if (finalParams.operatingTerritoryIds != null) {
      const rawIds = Array.isArray(finalParams.operatingTerritoryIds)
        ? finalParams.operatingTerritoryIds
        : String(finalParams.operatingTerritoryIds).split(",");
      const numericIds = rawIds.map(Number).filter(n => !Number.isNaN(n));
      if (numericIds.length > 0) {
        finalParams.operatingTerritoryIds = numericIds;
      } else {
        delete finalParams.operatingTerritoryIds;
      }
    }

    logger.log("🎯 Final search params initialized:", finalParams);
    logger.log("📅 Date/Time in final params:", {
      startDate: finalParams.startDate,
      endDate: finalParams.endDate,
      startTime: finalParams.startTime,
      endTime: finalParams.endTime,
      specificDate: finalParams.specificDate,
      fromParsedDates: parsedDates,
    });

    return finalParams;
  });

  // Get currencies for reference (not used for display - each space uses its own currency)
  const { currencies: _currencies } = useCurrencies();

  const buildSearchParamsFromText = useCallback(text => {
    const keyword = text.trim();
    if (!keyword) return {};

    // Always search across all parameters - backend does OR search
    return {
      addressContains: keyword,
      buildingName: keyword,
      hostOrgName: keyword,
      spaceName: keyword,
      cityOrTown: keyword,
      country: keyword,
    };
  }, []);

  const fetchWorkspaces = useCallback(
    async sortValue => {
      try {
        setLoading(true);

        const [sortField, sortDirection] = (sortValue || "createdAt:desc").split(":");
        const appliedSort = `${sortField},${sortDirection}`;

        const baseParams = {
          ...searchParams,
          pageSize,
          pageNumber: Math.max(0, currentPage - 1),
          paged: true,
          sort: appliedSort, // API expects "field,direction"
        };

        if (!showPublishedStatusFilter) {
          baseParams.status = "PUBLISHED";
        }

        Object.keys(baseParams).forEach(key => {
          if (
            baseParams[key] === undefined ||
            baseParams[key] === null ||
            baseParams[key] === "" ||
            (Array.isArray(baseParams[key]) && baseParams[key].length === 0)
          ) {
            delete baseParams[key];
          }
        });

        const response =
          mode === "authenticated"
            ? await spaceManagementService.searchSpaces(baseParams)
            : await spaceManagementService.searchPublicSpacesUnauthenticated(baseParams);

        const dataArray = Array.isArray(response?.data?.content)
          ? response.data.content
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : Array.isArray(response?.data)
              ? response.data
              : [];

        const transformed = dataArray.map(mapSpaceForDisplay).filter(Boolean);
        setWorkspaces(transformed);
        setTotalCount(response?.data?.totalElements ?? transformed.length);

        if (transformed.length === 0) {
          trackAnalyticsEvent(AnalyticsEvents.SEARCH_ZERO_RESULTS, {
            [AnalyticsProps.LOCATION]: searchParams.addressContains || searchParams.location,
            category_id: searchParams.categoryId,
            subcategory_id: searchParams.subCategoryId,
            filters: searchParams,
          });
        }
      } catch (error) {
        // The service refuses to search when we do not know which market to
        // serve. Show the country picker rather than an error/empty grid — the
        // visitor must choose a market before any inventory is fetched.
        if (error?.code === "MARKET_UNRESOLVED" || error?.name === "MarketUnresolvedError") {
          logger.log("SpacesSearchView: market unresolved — showing country picker");
          setWorkspaces([]);
          setTotalCount(0);
          setDetectedCountry("");
          setShowCountryBanner(true);
          return;
        }

        logger.error("SpacesSearchView: Error fetching workspaces", error);
        setWorkspaces([]);
        setTotalCount(0);
        trackAnalyticsEvent(AnalyticsEvents.SEARCH_ZERO_RESULTS, {
          [AnalyticsProps.LOCATION]: searchParams.addressContains || searchParams.location,
          error: error.message,
        });
      } finally {
        setLoading(false);
      }
    },
    [searchParams, pageSize, currentPage, mode, showPublishedStatusFilter, mapSpaceForDisplay]
  );

  useEffect(() => {
    fetchWorkspaces(currentSort);
  }, [fetchWorkspaces, currentSort]);

  useEffect(() => {
    if (!shouldShowSearchBar) {
      return;
    }

    const fromHeroSearch = sessionStorage.getItem("fromHeroSearch");
    if (fromHeroSearch === "true") {
      setTimeout(() => {
        sessionStorage.removeItem("fromHeroSearch");
      }, 100);
    }
  }, [shouldShowSearchBar]);

  // Fetch service categories on mount
  // Use Redux data when available, fetch locally otherwise
  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      if (mode === "authenticated" && reduxCategories.length > 0) {
        setServiceCategories(reduxCategories);
        return;
      }

      try {
        let data = [];

        if (mode === "authenticated") {
          try {
            const response = await billingService.getAllServiceCategories({ type: "END_USER" });
            if (response?.data?.data) {
              data = response.data.data;
            }
          } catch {
            // Fallback to old endpoint
          }
        }

        if (data.length === 0) {
          const response =
            mode === "unauthenticated"
              ? await billingService.getPublicServiceCategories({ orderBy: "name:desc" })
              : await billingService.getServiceCategories({ orderBy: "name:desc" });
          if (cancelled) return;
          if (response.data) {
            if (Array.isArray(response.data)) data = response.data;
            else if (Array.isArray(response.data.content)) data = response.data.content;
            else if (Array.isArray(response.data.data)) data = response.data.data;
          }
        }

        if (!cancelled) {
          setServiceCategories(data.sort((a, b) => (b.name || "").localeCompare(a.name || "")));
        }
      } catch {
        if (!cancelled) setServiceCategories([]);
      }
    };

    loadCategories();
    return () => {
      cancelled = true;
    };
  }, [mode, reduxCategories]);

  // Load subcategories from Redux if available
  useEffect(() => {
    if (mode === "authenticated" && reduxSubcategories.length > 0) {
      setServiceSubcategories(reduxSubcategories);
    }
  }, [mode, reduxSubcategories]);

  // Format price labels for filter chips - show generic currency label since each space has its own currency
  const formatCurrencyLabel = useCallback(value => `${value}`, []);

  const shuffleArray = useCallback(list => {
    if (!Array.isArray(list) || list.length === 0) return [];
    const copy = [...list];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }, []);

  // Build stable suggestion list (no random reordering to avoid UI glitches)
  const getSuggestionList = useCallback(() => {
    return [];
  }, []);

  const [allAvailableFilters, setAllAvailableFilters] = useState([]);
  const [_activeFilterTypes, setActiveFilterTypes] = useState(new Set());
  const hasInitializedFiltersRef = useRef(false);

  // Only set filters once when service categories are loaded, don't shuffle on every change
  useEffect(() => {
    const suggestions = getSuggestionList();
    if (suggestions.length > 0 && !hasInitializedFiltersRef.current) {
      // Only shuffle once on initial load
      setAllAvailableFilters(shuffleArray(suggestions));
      hasInitializedFiltersRef.current = true;
    } else if (suggestions.length > 0) {
      // After initial load, update without shuffling to prevent UI jumps
      setAllAvailableFilters(suggestions);
    }
  }, [getSuggestionList, shuffleArray]);

  // Fetch location suggestions from Google Places
  // Filter chip handlers
  // Helpers to parse filter value strings (e.g., "minPrice:1000,maxPrice:2500")
  const parseFilterValue = value => {
    if (!value) return [];
    return value.split(",").map(part => {
      const [key, rawVal] = part.split(":");
      const parsed =
        rawVal === "true" ? true : rawVal === "false" ? false : isNaN(Number(rawVal)) ? rawVal : Number(rawVal);
      return { key, value: parsed };
    });
  };

  // Remove filters of a given predicate (e.g., same type or includes a specific key)
  const removeFilters = predicate => {
    setActiveFilters(prev => prev.filter(f => !predicate(f)));
    setActiveFilterIds(prev => {
      const next = new Set(
        [...prev].filter(id => {
          const target = activeFilters.find(f => f.id === id);
          return target ? !predicate(target) : true;
        })
      );
      return next;
    });
  };

  const applyFilterToParams = (filter, isAdding) => {
    const parsedEntries = parseFilterValue(filter.value);

    setSearchParams(prev => {
      const next = { ...prev };

      // Remove previously set keys for this filter when toggling off
      const removeAppliedKeys = keys => {
        keys.forEach(key => {
          if (key === "categoryId") {
            delete next.categoryId;
            delete next.subCategoryId;
            delete next.filterOptionIds; // options belong to a subcategory that's now out of scope
          } else if (key === "minPrice" || key === "maxPrice" || key === "minCapacity") {
            next[key] = "";
          } else {
            delete next[key];
          }
        });
      };

      if (!isAdding) {
        removeAppliedKeys(filter.appliedKeys || []);
        return next;
      }

      // When adding, clean up conflicting filters first (e.g., only one price/capacity/category at a time)
      if (filter.type === "price") {
        removeFilters(f => f.type === "price" && f.id !== filter.id);
        removeAppliedKeys(["minPrice", "maxPrice"]);
      }
      if (filter.type === "capacity") {
        removeFilters(f => f.type === "capacity" && f.id !== filter.id);
        removeAppliedKeys(["minCapacity"]);
      }
      if (filter.type === "category") {
        removeFilters(f => f.type === "category" && f.id !== filter.id);
        removeAppliedKeys(["categoryId", "subCategoryId"]);
      }

      const appliedKeys = [];
      parsedEntries.forEach(entry => {
        const { key, value } = entry;
        appliedKeys.push(key);
        if (key === "sortBy") {
          setSortBy(value);
        } else if (key === "categoryId") {
          next.categoryId = value;
          next.subCategoryId = undefined;
        } else if (key === "minPrice" || key === "maxPrice" || key === "minCapacity") {
          next[key] = value.toString();
        } else {
          next[key] = value;
        }
      });

      // Track applied keys for clean removal later
      filter.appliedKeys = appliedKeys;
      return next;
    });
  };

  const handleFilterChange = filterId => {
    const filter = allAvailableFilters.find(f => f.id === filterId);
    if (!filter) return;

    const isActive = activeFilterIds.has(filterId);

    if (isActive) {
      // Toggle off
      applyFilterToParams(filter, false);
      setActiveFilterIds(prev => {
        const next = new Set(prev);
        next.delete(filterId);
        return next;
      });
      setActiveFilters(prev => prev.filter(f => f.id !== filterId));
    } else {
      // Toggle on
      applyFilterToParams(filter, true);
      setActiveFilterIds(prev => new Set(prev).add(filterId));
      setActiveFilters(prev => [...prev, { ...filter }]);
    }

    setCurrentPage(1);
  };

  // Host filter (set by clicking a card's host name, or ?hostOrganizationId= in the URL)
  const hostFilterId = showPublishedStatusFilter ? null : searchParams.hostOrganizationId || null;
  const hostFilterName = useMemo(() => {
    if (!hostFilterId) return "";
    if (hostFilterLabel?.id === hostFilterId && hostFilterLabel.name) return hostFilterLabel.name;
    // After a refresh/shared link only the id is known — read the name off the results
    const match = workspaces.find(space => (space?.organization?.id || space?.organizationId) === hostFilterId);
    return match?.organization?.name || match?.organizationName || "";
  }, [hostFilterId, hostFilterLabel, workspaces]);

  const clearHostFilter = () => {
    setHostFilterLabel(null);
    setSearchParams(prev => {
      const next = { ...prev };
      delete next.hostOrganizationId;
      delete next.hostOrgName;
      return next;
    });
    setCurrentPage(1);

    const query = new URLSearchParams(history.location.search);
    query.delete("hostOrganizationId");
    query.delete("hostOrgName");
    history.replace({ search: query.toString() });
    // fetchWorkspaces will be called automatically by useEffect when searchParams changes
  };

  // Clear filter by id
  const clearFilter = filterId => {
    const filter = activeFilters.find(f => f.id === filterId);
    if (!filter) return;
    applyFilterToParams(filter, false);
    setActiveFilters(prev => prev.filter(f => f.id !== filterId));
    setActiveFilterIds(prev => {
      const next = new Set(prev);
      next.delete(filterId);
      return next;
    });
    setCurrentPage(1);
  };

  // Get visible filters based on what's already selected
  const filters = useMemo(() => {
    return allAvailableFilters;
  }, [allAvailableFilters]);

  const clearAllFilters = () => {
    setActiveFilterIds(new Set());
    setActiveFilters([]);
    setActiveFilterTypes(new Set()); // Reset active filter types
    setSearchFilter("all");
    setFilterState({
      priceRange: [0, 10000],
      capacity: 1,
      amenities: [],
      publishedStatus: [],
      featuredStatus: [],
      topSpaces: false,
      duration: [],
      categoryId: null,
    });
    setSearchParams({
      categoryId: undefined, // Use categoryId instead of category
      subCategoryId: undefined, // Clear subcategory as well
      city: "",
      country: "",
      minPrice: "",
      maxPrice: "",
      minCapacity: "",
      addressContains: "",
      // Don't include dates/times in reset - they're cleared explicitly
      startDate: "",
      endDate: "",
      specificDate: undefined,
      startTime: "",
      endTime: "",
      ...initialFilters, // Restore only initial filters from parent
    });

    // Clear localStorage data when user explicitly clears filters (only for unauthenticated mode)
    if (mode !== "authenticated") {
      localStorage.removeItem("searchDates");
      localStorage.removeItem("searchFilters");
    }
    // fetchWorkspaces will be called automatically by useEffect when searchParams changes
  };

  // Handle applying filters from IllustrativeFilterDialog
  const handleApplyFilters = useCallback(
    filters => {
      setFilterState(filters);
      setSearchParams(prev => {
        const next = { ...prev };

        // Apply price range
        if (filters.priceRange && Array.isArray(filters.priceRange) && filters.priceRange.length === 2) {
          next.minPrice = filters.priceRange[0] > 0 ? filters.priceRange[0].toString() : "";
          next.maxPrice = filters.priceRange[1] < 10000 ? filters.priceRange[1].toString() : "";
        } else {
          next.minPrice = "";
          next.maxPrice = "";
        }

        // Apply capacity
        if (filters.capacity && filters.capacity > 1) {
          next.minCapacity = filters.capacity.toString();
        } else {
          next.minCapacity = "";
        }

        // Apply amenities (if the API supports it)
        // Note: You may need to adjust this based on your API's amenity parameter name
        if (filters.amenities && filters.amenities.length > 0) {
          // Assuming the API accepts an array of amenity IDs
          next.amenityIds = filters.amenities.map(a => a.id || a);
        } else {
          delete next.amenityIds;
        }

        // Apply duration filters (API expects an array of duration strings)
        if (filters.duration && Array.isArray(filters.duration) && filters.duration.length > 0) {
          next.duration = filters.duration;
        } else {
          delete next.duration;
        }

        // Apply featured/top spaces
        if (filters.topSpaces) {
          next.topSpaces = true;
        } else {
          delete next.topSpaces;
        }

        if (filters.featuredStatus && filters.featuredStatus.includes("FEATURED")) {
          next.featured = true;
        } else {
          delete next.featured;
        }

        // Apply published status (only for authenticated mode with showPublishedStatusFilter)
        if (showPublishedStatusFilter && filters.publishedStatus && filters.publishedStatus.length > 0) {
          // If multiple statuses selected, use the first one or handle as needed
          next.status = filters.publishedStatus[0];
        } else if (!showPublishedStatusFilter) {
          // For unauthenticated or authenticated without published filter, always use PUBLISHED
          next.status = "PUBLISHED";
        }

        // Apply categoryId
        if (filters.categoryId) {
          next.categoryId = filters.categoryId;
        } else {
          delete next.categoryId;
        }

        // Apply subCategoryId
        if (filters.subCategoryId) {
          next.subCategoryId = filters.subCategoryId;
        } else {
          delete next.subCategoryId;
        }

        // Apply filterOptionIds (fulfillment & vehicle options)
        if (filters.filterOptionIds && filters.filterOptionIds.length > 0) {
          next.filterOptionIds = filters.filterOptionIds;
        } else {
          delete next.filterOptionIds;
        }

        return next;
      });
      setCurrentPage(1);
      setShowFilters(false);
    },
    [showPublishedStatusFilter]
  );

  const fetchSpaceSuggestions = useCallback(
    async query => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery) {
        setSpaceSuggestions([]);
        return;
      }

      if (spaceSuggestionAbortRef.current) {
        spaceSuggestionAbortRef.current.abort();
      }

      const controller = new AbortController();
      spaceSuggestionAbortRef.current = controller;

      setIsFetchingSpaceSuggestions(true);

      try {
        const textFilters = buildSearchParamsFromText(trimmedQuery);
        const params = {
          ...textFilters,
          pageSize: 5,
          pageNumber: 0,
          paged: true,
        };

        if (!showPublishedStatusFilter) {
          params.status = "PUBLISHED";
        }

        const response =
          mode === "authenticated"
            ? await spaceManagementService.searchSpaces(params)
            : await spaceManagementService.searchPublicSpacesUnauthenticated(params);

        if (controller.signal.aborted) return;

        const dataArray = Array.isArray(response?.data?.content)
          ? response.data.content
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : Array.isArray(response?.data)
              ? response.data
              : [];

        const suggestions = dataArray.slice(0, 5).map(buildSuggestionFromSpace).filter(Boolean);
        setSpaceSuggestions(suggestions);
      } catch (error) {
        // Market unresolved — open the picker instead of surfacing an error.
        if (error?.code === "MARKET_UNRESOLVED" || error?.name === "MarketUnresolvedError") {
          if (!controller.signal.aborted) {
            setIsFetchingSpaceSuggestions(false);
          }
          setDetectedCountry("");
          setShowCountryBanner(true);
          return;
        }
        if (error.name !== "AbortError") {
          logger.error("SpacesSearchView: Error fetching space suggestions", error);
          setSpaceSuggestions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsFetchingSpaceSuggestions(false);
        }
      }
    },
    [mode, buildSuggestionFromSpace, buildSearchParamsFromText, showPublishedStatusFilter]
  );

  useEffect(() => {
    if (!isSearchDialogOpen) {
      if (spaceSuggestionAbortRef.current) {
        spaceSuggestionAbortRef.current.abort();
        spaceSuggestionAbortRef.current = null;
      }
      setSpaceSuggestions([]);
      setIsFetchingSpaceSuggestions(false);
      return;
    }

    const trimmed = searchText.trim();
    if (trimmed.length < 2) {
      if (spaceSuggestionAbortRef.current) {
        spaceSuggestionAbortRef.current.abort();
        spaceSuggestionAbortRef.current = null;
      }
      setSpaceSuggestions([]);
      setIsFetchingSpaceSuggestions(false);
      return;
    }

    if (spaceSuggestionDebounceRef.current) {
      clearTimeout(spaceSuggestionDebounceRef.current);
    }

    spaceSuggestionDebounceRef.current = setTimeout(() => {
      fetchSpaceSuggestions(trimmed);
    }, 300);

    return () => {
      if (spaceSuggestionDebounceRef.current) {
        clearTimeout(spaceSuggestionDebounceRef.current);
        spaceSuggestionDebounceRef.current = null;
      }
    };
  }, [searchText, isSearchDialogOpen, fetchSpaceSuggestions]);

  useEffect(() => {
    if (isSearchDialogOpen && !wasDialogOpenRef.current) {
      // Only set preset if current searchText is empty, otherwise keep user's current input
      const currentSearchText = searchText?.trim() || "";
      if (currentSearchText === "") {
        const preset =
          searchParams.addressContains ||
          searchParams.buildingName ||
          searchParams.spaceName ||
          searchParams.hostOrgName ||
          "";
        if (preset) {
          setSearchText(preset);
        }
      }
    }

    if (!isSearchDialogOpen && wasDialogOpenRef.current) {
      setSpaceSuggestions([]);
      setIsFetchingSpaceSuggestions(false);
    }

    wasDialogOpenRef.current = isSearchDialogOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchDialogOpen, searchParams]);

  // Handle sort change
  const handleSortChange = newSortBy => {
    setSortBy(newSortBy);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = pageData => {
    setCurrentPage(pageData.selected);
  };

  // Transform workspaces for map view (memoized)
  const transformWorkspacesForMap = useMemo(() => {
    const filtered = workspaces.filter(space => space.coordinates?.latitude && space.coordinates?.longitude);
    return filtered.map(space => ({
      id: space.id,
      name: space.displayName || space.name,
      location: space.location,
      pricing: space.pricing,
      capacity: space.capacity,
      image: space.image,
      status: space.status,
      coordinates: space.coordinates,
      instanceCount: space.instanceCount || 0,
      instances: space.instances || [],
    }));
  }, [workspaces]);

  // Handle search text submission
  const handleSearchTextSubmit = text => {
    if (!text) return;
    const keyword = text.trim();
    if (!keyword) return;

    setSearchText(keyword);
    const textParams = buildSearchParamsFromText(keyword);

    setSearchParams(prev => {
      const next = { ...prev };
      delete next.addressContains;
      delete next.buildingName;
      delete next.hostOrgName;
      delete next.hostOrganizationId;
      delete next.spaceName;
      delete next.cityOrTown;
      delete next.country;
      return { ...next, ...textParams };
    });
    setSpaceSuggestions([]);
    setIsFetchingSpaceSuggestions(false);
    setCurrentPage(1);
    setIsSearchDialogOpen(false);
  };

  const handleSpaceSuggestionSelect = suggestion => {
    if (!suggestion) return;
    const keyword = suggestion.name || searchText;
    handleSearchTextSubmit(keyword);
  };

  // Toggle a subcategory-filter option (MOVE category). Stored as a numeric array in
  // searchParams.filterOptionIds; the service joins it to filterOptionIds=10,11,201.
  const toggleFilterOption = rawOptionId => {
    const optionId = Number(rawOptionId);
    setSearchParams(prev => {
      const current = Array.isArray(prev.filterOptionIds) ? prev.filterOptionIds : [];
      const next = current.includes(optionId) ? current.filter(id => id !== optionId) : [...current, optionId];
      const updated = { ...prev };
      if (next.length > 0) {
        updated.filterOptionIds = next;
      } else {
        delete updated.filterOptionIds;
      }
      return updated;
    });
    setCurrentPage(1);
  };

  // Apply the selected operating territories (MOVE). Stable identity so the search bar's
  // prune effect doesn't re-run every render.
  const handleTerritoriesChange = useCallback(ids => {
    setSearchParams(prev => {
      const next = { ...prev };
      if (ids && ids.length) next.operatingTerritoryIds = ids;
      else delete next.operatingTerritoryIds;
      return next;
    });
    setCurrentPage(1);
  }, []);

  // Handle featured toggle
  const handleToggleFeatured = async space => {
    try {
      const newFeaturedStatus = !space.featured;
      await spaceManagementService.toggleFeaturedSpace(space.id, newFeaturedStatus);

      // Update the local state to reflect the change
      setWorkspaces(prevWorkspaces =>
        prevWorkspaces.map(w => (w.id === space.id ? { ...w, featured: newFeaturedStatus } : w))
      );

      // Show success message (you might want to add a toast notification here)
      logger.log(`Space ${space.id} ${newFeaturedStatus ? "marked as" : "unmarked as"} featured`);
    } catch (error) {
      logger.error("Error toggling featured status:", error);
      // Show error message (you might want to add a toast notification here)
    }
  };

  // Handle publish/unpublish toggle
  const handleTogglePublish = async space => {
    try {
      const newStatus = space.status === "PUBLISHED" ? "UNPUBLISHED" : "PUBLISHED";

      if (newStatus === "PUBLISHED") {
        await spaceManagementService.publishSpace(space.id);
      } else {
        await spaceManagementService.unpublishSpace(space.id);
      }

      // Update the local state to reflect the change
      setWorkspaces(prevWorkspaces =>
        prevWorkspaces.map(w => (w.id === space.id ? { ...w, status: newStatus, propertyStatus: newStatus } : w))
      );

      // Show success message (you might want to add a toast notification here)
      logger.log(`Space ${space.id} ${newStatus.toLowerCase()}`);
    } catch (error) {
      logger.error("Error toggling publish status:", error);
      // Show error message (you might want to add a toast notification here)
    }
  };

  // Render list content
  const renderListContent = () => {
    // For admin Properties screen (showPublishedStatusFilter=true), don't show book button, show featured toggle instead
    const isAdminView = showPublishedStatusFilter;
    // Show book button for authenticated users (not admin) and unauthenticated users (opens space detail)
    const shouldShowBookButton = !isAdminView;

    if (viewMode === "grid") {
      return (
        <GridView
          spaces={workspaces}
          onSelectSpace={onSpaceClick}
          onBookSpace={shouldShowBookButton ? onSpaceClick : null}
          onToggleFeatured={isAdminView ? handleToggleFeatured : null}
          onTogglePublish={isAdminView ? handleTogglePublish : null}
          onHostClick={isAdminView ? null : handleHostClick}
        />
      );
    }

    // List view - use SpaceCard components in a vertical layout with smooth staggered reveal
    return (
      <motion.div
        className="space-y-4"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.08,
              delayChildren: 0.05,
            },
          },
        }}
      >
        {workspaces.map((workspace, index) => (
          <motion.div
            key={`${workspace.id}-${index}`}
            variants={{
              hidden: { opacity: 0, y: 24, scale: 0.98 },
              visible: {
                opacity: 1,
                y: 0,
                scale: 1,
                transition: { type: "spring", stiffness: 300, damping: 24 },
              },
            }}
            className="w-full"
          >
            <SpaceCard
              space={workspace}
              onClick={() => onSpaceClick(workspace)}
              onBookSpace={shouldShowBookButton ? onSpaceClick : null}
              onToggleFeatured={isAdminView ? handleToggleFeatured : null}
              onTogglePublish={isAdminView ? handleTogglePublish : null}
              onZoomTo={space => setFocusedSpace(space)}
              onHostClick={isAdminView ? null : handleHostClick}
            />
          </motion.div>
        ))}
      </motion.div>
    );
  };

  return (
    <div className={`${mode === "unauthenticated" ? "" : "min-h-screen"} bg-background flex flex-col`}>
      {/* Custom scrollbar styles for list view */}
      <style>{`
                .spaces-list-container::-webkit-scrollbar {
                    width: 8px;
                }
                .spaces-list-container::-webkit-scrollbar-track {
                    background: #f3f4f6;
                    border-radius: 4px;
                }
                .spaces-list-container::-webkit-scrollbar-thumb {
                    background: #d1d5db;
                    border-radius: 4px;
                }
                .spaces-list-container::-webkit-scrollbar-thumb:hover {
                    background: #9ca3af;
                }
                .spaces-list-container {
                    scroll-behavior: smooth;
                }
            `}</style>
      {/* Header */}
      <PageHeader
        title={hostFilterId ? `Spaces by ${hostFilterName || "this host"}` : title}
        totalCount={totalCount}
        subtitle={subtitle}
        breadcrumbs={defaultBreadcrumbs}
        rightContent={
          <>
            <div className="inline-flex bg-gray-200 rounded-md">
              <button
                onClick={() => {
                  setViewMode("list");
                  trackAnalyticsEvent(AnalyticsEvents.SEARCH_VIEW_MODE_CHANGED, { mode: "list" });
                }}
                className={`flex items-center justify-center p-2 transition-all duration-200 ${
                  viewMode === "list" ? "bg-black text-white shadow-sm" : "text-black hover:bg-gray-300"
                }`}
                title="List"
              >
                <ListIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setViewMode("grid");
                  trackAnalyticsEvent(AnalyticsEvents.SEARCH_VIEW_MODE_CHANGED, { mode: "grid" });
                }}
                className={`flex items-center justify-center p-2 transition-all duration-200 ${
                  viewMode === "grid" ? "bg-black text-white shadow-sm" : "text-black hover:bg-gray-300"
                }`}
                title="Grid"
              >
                <Grid3x3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setViewMode("map");
                  trackAnalyticsEvent(AnalyticsEvents.SEARCH_VIEW_MODE_CHANGED, { mode: "map" });
                }}
                className={`flex items-center justify-center p-2 transition-all duration-200 ${
                  viewMode === "map" ? "bg-black text-white shadow-sm" : "text-black hover:bg-gray-300"
                }`}
                title="Map"
              >
                <MapIcon className="w-4 h-4" />
              </button>
            </div>
            {/* Country selector chip — always accessible so users can see and switch markets */}
            <CountrySelectorBadge
              countryCode={selectedBrowseCountry}
              onCountryChange={(code) => {
                saveBrowseCountry(code);
                setSelectedBrowseCountry(code);
                setCurrentPage(1);
                setSearchParams(prev => ({ ...prev }));
              }}
              onOpenModal={() => setShowCountryBanner(true)}
            />
            <SortDropdown options={sortOptions} value={currentSort} onChange={handleSortChange} />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(true)}
              className="bg-white hover:bg-gray-50"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </>
        }
        bottomContent={
          <>
            {shouldShowSearchBar && (
              <div className="pt-4 pb-3">
                {showCountryBanner && (
                  <CountryPickerBanner
                    forceOpen={showCountryBanner}
                    detectedCountry={detectedCountry}
                    onCountrySelect={(code) => {
                      saveBrowseCountry(code);
                      setSelectedBrowseCountry(code);
                      setShowCountryBanner(false);
                      setCurrentPage(1);
                      // Clear stale text search and refresh query so country's spaces show immediately
                      setSearchParams(prev => {
                        const next = { ...prev };
                        delete next.addressContains;
                        delete next.buildingName;
                        delete next.hostOrgName;
                        delete next.spaceName;
                        delete next.cityOrTown;
                        delete next.country;
                        return next;
                      });
                      fetchWorkspaces(currentSort);
                    }}
                    onDismiss={() => setShowCountryBanner(false)}
                  />
                )}
                <motion.div
                  layoutId="search-bar-hero-to-filter"
                  transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  className="w-full"
                >
                  <SpaceSearchBar
                    isAuthenticated={mode === "authenticated"}
                    initialSearchQuery={searchParams.addressContains || searchParams.spaceName || ""}
                    initialSearchFilter={searchFilter}
                    initialCategoryId={searchParams.categoryId ? String(searchParams.categoryId) : ""}
                    initialSubcategoryId={searchParams.subCategoryId ? String(searchParams.subCategoryId) : ""}
                    initialDateRange={
                      searchParams.specificDate
                        ? { specificDate: searchParams.specificDate }
                        : searchParams.startDate && searchParams.endDate
                        ? { startDate: searchParams.startDate, endDate: searchParams.endDate }
                        : null
                    }
                    initialFilterOptionIds={searchParams.filterOptionIds}
                    initialDropoffLocation={searchParams.dropoffLocation}
                    onSearch={(payload) => {
                      if (payload.searchFilter) {
                        setSearchFilter(payload.searchFilter);
                      }
                      setSearchParams(prev => {
                        const next = { ...prev };
                        delete next.addressContains;
                        delete next.hostOrgName;
                        delete next.hostOrganizationId;
                        delete next.spaceName;
                        delete next.buildingName;
                        delete next.cityOrTown;
                        delete next.country;

                        const q = payload.query || "";
                        const filterKey = payload.searchFilter || "all";
                        if (q) {
                          if (filterKey === "organizations") {
                            next.hostOrgName = q;
                          } else if (filterKey === "buildings") {
                            next.buildingName = q;
                          } else if (filterKey === "spaces") {
                            next.spaceName = q;
                          } else if (filterKey === "cities") {
                            next.cityOrTown = q;
                          } else if (filterKey === "countries") {
                            next.country = q;
                          } else {
                            next.addressContains = q;
                            next.hostOrgName = q;
                            next.spaceName = q;
                            next.buildingName = q;
                            next.cityOrTown = q;
                            next.country = q;
                          }
                        }

                        if (payload.location) {
                          next.addressContains = payload.location;
                        }

                        return {
                          ...next,
                          categoryId: payload.categoryId ? parseInt(payload.categoryId) : undefined,
                          subCategoryId: payload.subcategoryId ? parseInt(payload.subcategoryId) : undefined,
                          dropoffLocation: payload.dropoffLocation || undefined,
                          startDate: payload.dateRange?.startDate || undefined,
                          endDate: payload.dateRange?.endDate || undefined,
                          specificDate: payload.dateRange?.specificDate || undefined,
                          filterOptionIds: payload.filterOptionIds && payload.filterOptionIds.length > 0 ? payload.filterOptionIds : undefined,
                        };
                      });
                      setCurrentPage(1);
                    }}
                  />
                </motion.div>
              </div>
            )}
            {hostFilterId && (
              <div className="flex flex-wrap gap-2 mt-4">
                <Badge variant="secondary" className="px-3 py-1 bg-white text-gray-900 border border-gray-300">
                  Host: {hostFilterName || `#${hostFilterId}`}
                  <button
                    onClick={clearHostFilter}
                    className="ml-2 hover:text-destructive"
                    aria-label="Remove host filter"
                  >
                    ×
                  </button>
                </Badge>
              </div>
            )}
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {activeFilters.map(filter => (
                  <Badge
                    key={filter.id}
                    variant="secondary"
                    className="px-3 py-1 bg-white text-gray-900 border border-gray-300"
                  >
                    {filter.label}
                    <button onClick={() => clearFilter(filter.id)} className="ml-2 hover:text-destructive">
                      ×
                    </button>
                  </Badge>
                ))}
                <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-auto py-1">
                  Clear all
                </Button>
              </div>
            )}
            {filters.length > 0 && (
              <motion.div className="flex gap-2 mt-2 overflow-x-auto pb-2 scrollbar-hide" layout>
                <AnimatePresence mode="popLayout">
                  {filters.map(filter => (
                    <FilterChip
                      key={filter.id}
                      label={filter.label}
                      active={activeFilterIds.has(filter.id)}
                      onClick={() => handleFilterChange(filter.id)}
                      isVisible={true}
                      icon={filter.icon}
                    />
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </>
        }
      />
      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-3 w-full flex-1">
        {loading ? (
          viewMode === "list" ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              <div className="lg:col-span-5 space-y-4">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <SpaceCardSkeleton key={`list-skel-${idx}`} />
                ))}
              </div>
              <div className="hidden lg:block lg:col-span-7 sticky top-32 self-start mb-36">
                <div className="h-[calc(100vh-12rem)] rounded-xl border border-dashed border-gray-200 bg-gray-50 animate-pulse" />
              </div>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, idx) => (
                <SpaceCardSkeleton key={`grid-skel-${idx}`} />
              ))}
            </div>
          ) : (
            <div className="h-[calc(100vh-8rem)] rounded-xl border border-dashed border-gray-200 bg-gray-50 animate-pulse" />
          )
        ) : workspaces.length === 0 ? (
          <div className="text-center py-20">
            <HEmptyState
              icon={Building2}
              title="No items found in your region"
              description="No spaces, services or events found matching your criteria. Try picking another country or clearing your filters."
              actionLabel="Select Country"
              onAction={() => setShowCountryBanner(true)}
            />
          </div>
        ) : (
          <>
            {viewMode === "list" ? (
              /* List view with map - Two-column layout, map takes more space on desktop */
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-5">
                  <div className="space-y-4">{renderListContent()}</div>

                  {/* Pagination below list */}
                  {!loading && workspaces.length > 0 && totalCount > pageSize && (
                    <div className="flex justify-center pt-4 pb-2 border-t border-gray-200 mt-4">
                      <Pagination
                        currentPage={currentPage}
                        totalCount={totalCount}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  )}
                </div>
                <div className="hidden lg:block lg:col-span-7 sticky top-32 self-start mb-36">
                  <div className="h-[calc(100vh-12rem)]">
                    <ReusableMapView
                      spaces={transformWorkspacesForMap}
                      onSpaceClick={onSpaceClick}
                      focusedSpace={focusedSpace}
                      onClearFocus={() => setFocusedSpace(null)}
                      className="animate-in fade-in-0 slide-in-from-bottom-4 duration-500"
                    />
                  </div>
                </div>
              </div>
            ) : viewMode === "grid" ? (
              /* Grid view - Full width grid layout with NO map */
              <>
                <div className="space-y-6">
                  <div>{renderListContent()}</div>
                </div>
                {/* Pagination for grid view */}
                {!loading && workspaces.length > 0 && totalCount > pageSize && (
                  <div className="flex justify-center mt-8">
                    <Pagination
                      currentPage={currentPage}
                      totalCount={totalCount}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            ) : (
              /* Map view - Full width map layout with rounded corners and full height */
              <>
                <div className="h-[calc(100vh-8rem)]">
                  <ReusableMapView
                    spaces={transformWorkspacesForMap}
                    onSpaceClick={onSpaceClick}
                    className="animate-in fade-in-0 slide-in-from-bottom-4 duration-700"
                  />
                </div>
                {/* Pagination for map view */}
                {!loading && workspaces.length > 0 && totalCount > pageSize && (
                  <div className="flex justify-center mt-4">
                    <Pagination
                      currentPage={currentPage}
                      totalCount={totalCount}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
      {/* IllustrativeFilterDialog */}
      <IllustrativeFilterDialog
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={clearAllFilters}
        initialFilters={filterState}
        totalResults={totalCount}
        showPublishedStatusFilter={showPublishedStatusFilter}
        categories={serviceCategories}
        subcategories={serviceSubcategories.length > 0 ? serviceSubcategories : reduxSubcategories}
        isAuthenticated={mode === "authenticated"}
      />
    </div>
  );
};

export default SpacesSearchView;
